import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { IconSailboat, IconShare3 } from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';

import '../../styles/SettingsPage.css';
import { ActionsModal } from '../../components/shared/ActionsModal';
import { ReactComponent as ConfigurationIcon } from '../../react-icons/gearConfigBasic.svg';
import { ReactComponent as LogoutIcon } from '../../react-icons/logoutIcon.svg';
import { ReactComponent as PaymentsIcon } from '../../react-icons/paymentIcon.svg';
import { ReactComponent as ProfileIcon } from '../../react-icons/profileIcon.svg';
import { ReactComponent as SailTrackLogo } from '../../react-icons/SailTrackLogo.svg';
import { ReactComponent as SecurityIcon } from '../../react-icons/security.svg';
import { ReactComponent as PrivacyIcon } from '../../react-icons/userPrivacy.svg';

export type SettingsOutletContext = {
  setHasUnsavedChanges: (dirty: boolean) => void;
  registerRevertHandler: (handler: (() => void) | null) => void;
};

const SettingsPageContent = () => {
  const { t } = useTranslation('common');
  const location = useLocation();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState(location.pathname.split('/')[2] || 'profile');
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [isLeaveModalOpen, setIsLeaveModalOpen] = useState(false);
  const pendingNavigationRef = useRef<(() => void) | null>(null);
  const revertHandlerRef = useRef<(() => void) | null>(null);

  useEffect(() => {
    const current = location.pathname.split('/')[2] || 'profile';
    setActiveTab(current);
  }, [location.pathname]);

  const setDirty = useCallback((dirty: boolean) => {
    setHasUnsavedChanges(dirty);
  }, []);

  const registerRevertHandler = useCallback((handler: (() => void) | null) => {
    revertHandlerRef.current = handler;
  }, []);

  const tabs = [
    {
      id: 'profile',
      label: t('nav.profile'),
      icon: <ProfileIcon className="nav-icon" aria-label={t('nav.profile')} />,
    },
    {
      id: 'configuration',
      label: t('nav.configuration'),
      icon: <ConfigurationIcon className="nav-icon" aria-label={t('nav.configuration')} />,
    },
    {
      id: 'boats',
      label: t('nav.boats', 'Boats'),
      icon: <IconSailboat className="nav-icon" aria-label={t('nav.boats', 'Boats')} stroke={1.5} />,
    },
    {
      id: 'data-privacy',
      label: t('nav.dataPrivacy'),
      icon: <PrivacyIcon className="nav-icon" aria-label={t('nav.dataPrivacy')} />,
    },
    {
      id: 'sharing',
      label: t('settings.privacy.shareData', 'Sharing'),
      icon: <IconShare3 className="nav-icon" aria-label={t('settings.privacy.shareData', 'Sharing')} stroke={1.5} />,
    },
    {
      id: 'security',
      label: t('nav.security'),
      icon: <SecurityIcon className="nav-icon" aria-label={t('nav.security')} />,
    },
    {
      id: 'payments',
      label: t('nav.paymentsSubscriptions'),
      icon: <PaymentsIcon className="nav-icon" aria-label={t('nav.paymentsSubscriptions')} />,
    },
  ];

  const attemptNavigation = useCallback(
    (navigationAction: () => void) => {
      if (!hasUnsavedChanges) {
        navigationAction();
        return;
      }
      pendingNavigationRef.current = navigationAction;
      setIsLeaveModalOpen(true);
    },
    [hasUnsavedChanges],
  );

  const confirmLeave = () => {
    revertHandlerRef.current?.();
    setHasUnsavedChanges(false);
    setIsLeaveModalOpen(false);
    const pending = pendingNavigationRef.current;
    pendingNavigationRef.current = null;
    pending?.();
  };

  const stayOnPage = () => {
    pendingNavigationRef.current = null;
    setIsLeaveModalOpen(false);
  };

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }
    const beforeUnloadHandler = (event: BeforeUnloadEvent) => {
      if (!hasUnsavedChanges) {
        return;
      }
      event.preventDefault();
      event.returnValue = '';
    };
    window.addEventListener('beforeunload', beforeUnloadHandler);
    return () => {
      window.removeEventListener('beforeunload', beforeUnloadHandler);
    };
  }, [hasUnsavedChanges]);

  const handleTabChange = (tabId: string) => {
    attemptNavigation(() => {
      setActiveTab(tabId);
      const path = tabId === 'profile' ? '/settings' : `/settings/${tabId}`;
      navigate(path);
    });
  };

  const handleLogoClick = () => {
    attemptNavigation(() => {
      setActiveTab('profile');
      navigate('/home');
    });
  };

  const handleLogout = () => {
    attemptNavigation(() => {
      localStorage.removeItem('token');
      navigate('/authUser');
    });
  };

  const handleHelpSupport = () => {
    attemptNavigation(() => {
      navigate('/help-support');
    });
  };

  const outletContext = useMemo<SettingsOutletContext>(
    () => ({
      setHasUnsavedChanges: setDirty,
      registerRevertHandler,
    }),
    [registerRevertHandler, setDirty],
  );

  return (
    <>
      <div className="settings-container">
        <div className="settings-sidebar">
          <button onClick={handleLogoClick} type="button" className="logo-button-settings">
            <div className="logo-container-settings">
              <SailTrackLogo
                className="logo-settings"
                role="img"
                aria-label="SailTrack Logo"
                focusable="false"
              />
              <span className="logo-title-settings">SailTrack</span>
            </div>
          </button>
          <h2 className="settings-title">{t('settings.app.title')}</h2>
          <div className="line-settings" />

          <nav className="settings-nav">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                type="button"
                className={`settings-nav-item ${activeTab === tab.id ? 'active' : ''}`}
                onClick={() => handleTabChange(tab.id)}
                aria-current={activeTab === tab.id ? 'page' : undefined}
                aria-label={tab.label}
              >
                {tab.icon}
                <span className="nav-label">{tab.label}</span>
                {activeTab === tab.id && <span className="underline-effect" />}
              </button>
            ))}
          </nav>
          <div className="line-navsettings-separator" />
          <button className="help-support-button-settings" onClick={handleHelpSupport}>
            <span className="help-support-text-settings">{t('settings.helpSupport', 'Help / Support')}</span>
          </button>
          <div className="logout-settings-container">
            <button
              type="button"
              className="logout-button logout-button-settings"
              onClick={handleLogout}
            >
              <LogoutIcon className="dropdown-icon" aria-label={t('nav.logout')} />
              <span>{t('nav.logout')}</span>
            </button>
          </div>
        </div>
        <div className="settings-content">
          <Outlet context={outletContext} />
        </div>
      </div>
      <ActionsModal
        isVisible={isLeaveModalOpen}
        title={t('settings.unsavedChanges.title')}
        message={t('settings.unsavedChanges.message')}
        actions={[
          {
            label: t('settings.unsavedChanges.stay'),
            onClick: stayOnPage,
            styleType: 'secondary',
          },
          {
            label: t('settings.unsavedChanges.discard'),
            onClick: confirmLeave,
            styleType: 'danger',
          },
        ]}
        onClose={stayOnPage}
      />
    </>
  );
};

export const SettingsPage = () => <SettingsPageContent />;
